import api from '@anm/api';
import { CurrentWaveEventParams, WaveEvent, WaveEventListParams } from '@anm/api/modules/waveEvents';
import isServer from '@anm/helpers/is/isServer';
import { actionChannel, call, fork, put, select, take } from '@anm/helpers/saga/effects';
import { buffers } from 'redux-saga';
import { ActionWithPayload } from 'store';

import vtConfig from '../../../config';
import calcStartEndDates from '../../../helpers/date/calcStartEndDates';
import getStringDate from '../../../helpers/getStringDate';
import { filterActions } from '../filter';
import { templateActions } from '../template';

import { waveEventsActions, FETCH_WAVE_EVENT_LIST, SET_CURRENT_TEMPLATE_FROM_WAVE_EVENT } from './actions';
import { selectCurrentWaveEvent, selectWaveEvents } from './selectors';

function* fetchWaveEventList(payload: WaveEventListParams) {
  try {
    const { weekDate: weekDateString } = payload;
    const { startDate, endDate } = calcStartEndDates(weekDateString);
    const startWeekDate = getStringDate(startDate);
    const endWeekDate = getStringDate(endDate);

    yield put(waveEventsActions.updateCurrentWeek(startWeekDate, endWeekDate));
    const currentWeekEvents = yield* select(selectWaveEvents(startWeekDate));

    if (!currentWeekEvents || isServer()) {
      yield put(waveEventsActions.getWaveEventList());

      const waveEventList = yield* call(api().waveEvents.getWaveEventList, {
        weekDate: startWeekDate,
        calendarBaseURL: vtConfig().urls.calendarData
      });

      yield put(waveEventsActions.getWaveEventListSuccess(waveEventList, waveEventList.length));
    }
  } catch (e) {
    yield put(waveEventsActions.getWaveEventListError(e));
  }
}

function* watchFetchWaveEventList() {
  const requestChan = yield* actionChannel(
    FETCH_WAVE_EVENT_LIST,
    buffers.expanding<ActionWithPayload<string, WaveEventListParams>>()
  );

  while (true) {
    const { payload } = yield* take(requestChan);

    yield call(fetchWaveEventList, payload);
  }
}

function* fetchCurrentWaveEvent({ weekDate, waveEventId }: CurrentWaveEventParams) {
  try {
    const calendarBaseURL = vtConfig().urls.calendarData;

    yield put(
      waveEventsActions.getCurrentWaveEvent({
        weekDate,
        waveEventId,
        calendarBaseURL
      })
    );

    const waveEventItem: WaveEvent = yield call(api().waveEvents.getWaveEvent, {
      weekDate,
      waveEventId,
      calendarBaseURL
    });

    yield put(waveEventsActions.getCurrentWaveEventSuccess(waveEventItem));
  } catch (e) {
    yield put(waveEventsActions.getCurrentWaveEventError(e));
  }
}

function* setCurrentTemplateFromWaveEvent() {
  const { previewId, categories } = yield select(selectCurrentWaveEvent);

  if (!previewId) return;

  yield put(filterActions.reset());
  yield put(
    templateActions.fetchTemplateView({
      templateIdOrSlug: previewId,
      templateListFilters: {
        types: 'service,system',
        category: categories && categories[0]
      }
    })
  );
}

function* watchSetCurrentTemplateFromWaveEvent() {
  const action: ActionWithPayload<string, CurrentWaveEventParams> = yield take(SET_CURRENT_TEMPLATE_FROM_WAVE_EVENT);

  yield call(fetchCurrentWaveEvent, action.payload);
  yield call(setCurrentTemplateFromWaveEvent);
}

const waveEventWatchers = () => [fork(watchFetchWaveEventList), fork(watchSetCurrentTemplateFromWaveEvent)];

export default waveEventWatchers;
